import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Instagram, Building2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function ContactPage() {
  const { t, lang } = useI18n();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", topic: "info", message: "", privacy_consent: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("Consenso privacy obbligatorio.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", topic: "info", message: "", privacy_consent: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">Contatti</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-brand leading-[0.98]">
            {t("contact.title")}
          </h1>
          <p className="mt-6 text-lg text-muted-ink">{t("contact.sub")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 lg:py-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <aside className="md:col-span-4">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">{t("contact.company_data")}</div>
          <div className="space-y-4 text-sm">
            {settings && (
              <>
                <div className="flex items-start gap-3">
                  <Building2 className="h-4 w-4 mt-0.5 text-muted-ink shrink-0" />
                  <div>
                    <div className="text-brand font-medium">{settings.company_legal_name}</div>
                    <div className="text-muted-ink text-xs">
                      P.IVA {settings.vat_number} · C.F. {settings.fiscal_code}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-ink shrink-0" />
                  <div className="text-brand">{settings.legal_address}</div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-4 w-4 mt-0.5 text-muted-ink shrink-0" />
                  <a href={`mailto:${settings.email}`} className="text-brand link-underline">
                    {settings.email}
                  </a>
                </div>
                {settings.instagram && (
                  <div className="flex items-start gap-3">
                    <Instagram className="h-4 w-4 mt-0.5 text-muted-ink shrink-0" />
                    <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="text-brand link-underline">
                      Instagram @bionutrix_
                    </a>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mt-8 border border-dashed border-soft p-4 text-xs text-muted-ink italic">
            {lang === "en"
              ? "Additional data (REA number, PEC, phone, hours) will be published when available."
              : "Ulteriori dati (numero REA, PEC, telefono, orari) verranno pubblicati quando disponibili."}
          </div>
        </aside>

        <form onSubmit={submit} className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
          <Input required placeholder={t("common.name")} value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none border-soft bg-white md:col-span-2" data-testid="contact-name" />
          <Input required type="email" placeholder={t("common.email")} value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none border-soft bg-white" data-testid="contact-email" />
          <Input placeholder={t("common.phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none border-soft bg-white" />
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">
              {t("contact.topic")}
            </label>
            <select
              value={form.topic}
              onChange={(e) => set("topic", e.target.value)}
              className="w-full h-9 border border-soft bg-white px-3 text-sm text-brand rounded-none"
              data-testid="contact-topic"
            >
              {Object.entries(t("contact.topics")).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <textarea
            required
            placeholder={t("common.message")}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            rows={5}
            className="md:col-span-2 border border-soft bg-white p-3 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-brand"
            data-testid="contact-message"
          />
          <label className="md:col-span-2 flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
            <input
              type="checkbox"
              required
              checked={form.privacy_consent}
              onChange={(e) => set("privacy_consent", e.target.checked)}
              className="mt-1"
              data-testid="contact-privacy"
            />
            <span>{t("common.privacy_consent")}</span>
          </label>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="rounded-full bg-brand text-white px-8 py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50 transition-colors"
            >
              {loading ? t("common.sending") : t("common.submit")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
